import LegalPage from "@/components/LegalPage";

const sections = [
  {
    id: "acceptance",
    title: "1. Acceptance of these Terms",
    body: (
      <>
        <p>
          These Terms of Use (&ldquo;Terms&rdquo;) govern your access to and use of
          <a href="https://www.ashrenltd.com" className="underline"> www.ashrenltd.com</a> and any
          related services, forms, or downloadable materials (collectively, the &ldquo;Site&rdquo;)
          provided by <strong>Ashren Consulting Ltd.</strong> (&ldquo;Ashren,&rdquo; &ldquo;we,&rdquo; &ldquo;our&rdquo;).
          By accessing or using the Site, you agree to be bound by these Terms. If you do not
          agree, please discontinue use.
        </p>
      </>
    ),
  },
  {
    id: "services",
    title: "2. Nature of our services",
    body: (
      <>
        <p>
          Ashren is a Canadian staffing and consulting firm. The Site exists to introduce our
          services, provide educational resources, and allow prospective clients and candidates to
          contact us. Nothing on the Site constitutes an offer of employment, a binding staffing
          engagement, legal advice, financial advice, or a guarantee of outcome. Any engagement is
          subject to a separate written agreement between you and Ashren.
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    title: "3. Eligibility",
    body: (
      <>
        <p>
          You must be at least 18 years of age and legally capable of entering into a binding
          contract to use the Site or submit any form. By submitting information, you represent
          that you meet these requirements and that any information you provide is truthful,
          accurate, and your own (or provided with proper authority).
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "4. Acceptable use",
    body: (
      <>
        <p>When using the Site, you agree that you will not:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Submit false, misleading, or fraudulent information;</li>
          <li>Use the Site to harass, defame, or infringe the rights of any person;</li>
          <li>Attempt to probe, scan, reverse-engineer, or interfere with the Site&rsquo;s security;</li>
          <li>Scrape, crawl, or systematically extract content or data from the Site without our prior written permission;</li>
          <li>Upload or transmit viruses, malware, or any other harmful code;</li>
          <li>Use the Site in violation of any applicable law or regulation (including Canadian privacy and anti-spam law).</li>
        </ul>
      </>
    ),
  },
  {
    id: "intellectual-property",
    title: "5. Intellectual property",
    body: (
      <>
        <p>
          All content on the Site — including text, graphics, logos (including the Ashren
          wordmark), images, PDFs, data compilations, and source code — is the property of Ashren
          Consulting Ltd. or its licensors and is protected by Canadian and international copyright,
          trademark, and other intellectual-property laws.
        </p>
        <p>
          You may view the Site for personal or internal business purposes and download the PDF
          resources we offer for your own non-commercial reference. Any other reproduction,
          redistribution, or derivative use requires our prior written consent.
        </p>
      </>
    ),
  },
  {
    id: "user-submissions",
    title: "6. Your submissions",
    body: (
      <>
        <p>
          By submitting information through any form on the Site (employer intake, candidate
          profile, contact, lead-magnet email capture), you grant Ashren a non-exclusive,
          royalty-free, worldwide license to use that information solely for the purposes
          described in our <a href="/privacy" className="underline">Privacy Policy</a>, including
          providing our services to you and, where you have given separate consent, representing
          candidate profiles to prospective employers.
        </p>
        <p>
          You represent that you own or have the necessary rights to any information you submit
          and that it does not infringe or violate the rights of any third party.
        </p>
      </>
    ),
  },
  {
    id: "third-party",
    title: "7. Third-party links and content",
    body: (
      <>
        <p>
          The Site may contain links to third-party websites, services, or resources (for example,
          Google Maps for our office location). Ashren does not control and is not responsible for
          the content, privacy practices, or terms of use of such third-party sites. Your use of
          any third-party site is at your own risk and governed by that third party&rsquo;s own terms.
        </p>
      </>
    ),
  },
  {
    id: "disclaimer",
    title: "8. Disclaimer of warranties",
    body: (
      <>
        <p>
          The Site, its content, and any downloadable materials are provided &ldquo;as is&rdquo; and
          &ldquo;as available&rdquo; without warranty of any kind, express or implied, including
          warranties of merchantability, fitness for a particular purpose, non-infringement, or
          uninterrupted or error-free operation.
        </p>
        <p>
          While we take reasonable care to keep salary data, market insights, and checklists
          accurate and current, the information is provided for general reference only and should
          not be relied on as the sole basis for any hiring, compensation, or procurement decision.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    title: "9. Limitation of liability",
    body: (
      <>
        <p>
          To the maximum extent permitted by applicable Canadian law, Ashren Consulting Ltd., its
          directors, officers, employees, and contractors will not be liable for any indirect,
          incidental, special, consequential, or punitive damages — including lost profits, lost
          revenue, lost data, or business interruption — arising out of or relating to your use of
          the Site, even if advised of the possibility of such damages.
        </p>
        <p>
          Our total aggregate liability arising out of your use of the Site (other than under a
          separate signed engagement agreement) is limited to one hundred Canadian dollars
          (CAD $100).
        </p>
      </>
    ),
  },
  {
    id: "indemnity",
    title: "10. Indemnity",
    body: (
      <>
        <p>
          You agree to indemnify and hold harmless Ashren Consulting Ltd. and its personnel from
          and against any claim, loss, liability, or expense (including reasonable legal fees)
          arising out of your breach of these Terms, your violation of any law, or your violation
          of the rights of a third party in connection with your use of the Site.
        </p>
      </>
    ),
  },
  {
    id: "termination",
    title: "11. Termination",
    body: (
      <>
        <p>
          We may suspend or terminate your access to the Site at any time, with or without notice,
          for any reason — including breach of these Terms. Sections relating to intellectual
          property, disclaimers, limitation of liability, indemnity, and governing law survive any
          termination.
        </p>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "12. Governing law and jurisdiction",
    body: (
      <>
        <p>
          These Terms are governed by the laws of the Province of British Columbia and the
          federal laws of Canada applicable therein, without regard to conflict-of-laws principles.
          You agree to submit to the exclusive jurisdiction of the courts of British Columbia,
          located in Vancouver, for any dispute arising out of or relating to these Terms or the
          Site.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "13. Changes to these Terms",
    body: (
      <>
        <p>
          We may update these Terms from time to time. The effective date at the top of this page
          indicates when they were last revised. Continued use of the Site after any change
          constitutes acceptance of the revised Terms.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "14. Contact us",
    body: (
      <>
        <p>Questions about these Terms can be directed to:</p>
        <p>
          <strong>Ashren Consulting Ltd.</strong><br />
          101, 2529 Kingsway<br />
          Vancouver, BC V5R 5H3<br />
          Canada<br />
          Email: <a href="mailto:Sales@ashrenltd.com" className="underline">Sales@ashrenltd.com</a><br />
          Phone: <a href="tel:+12364850134" className="underline">+1 236-485-0134</a>
        </p>
      </>
    ),
  },
];

export default function TermsOfUse() {
  return (
    <LegalPage
      eyebrow="Legal · Terms"
      title="Terms of Use"
      subtitle="The rules for using www.ashrenltd.com, submitting forms, and accessing our downloadable resources."
      effectiveDate="May 4, 2026"
      sections={sections}
      testId="terms-of-use-page"
    />
  );
}
